/**
 * MAHI showing a new user around Discover, once, on their first landing.
 *
 * The shape of it: she lifts off her seat in the bottom action row, moves to
 * whatever she's talking about, says one line, and moves on — ending back on
 * her seat, which is where you'll find her from then on. The screen dims apart
 * from the thing she's pointing at, so there is never a question of what "here"
 * means.
 *
 * Three rules it's built around:
 *
 *   1. Nothing advances on its own. Every step waits for a tap. A timer that
 *      moves on while you're still reading is the thing that makes a tour feel
 *      like it's happening *to* you, and this is the first screen of the
 *      product.
 *   2. Skip is visible on every step, and skipping counts as seen — she does
 *      not ask twice.
 *   3. It never blocks. If a target isn't on screen (empty deck, slow feed),
 *      that step is dropped rather than pointing at nothing.
 *
 * The dim is one element with a huge spread `box-shadow` rather than four
 * divs or an SVG mask: a single composited layer, no seams between panels, and
 * the cutout gets real rounded corners for free.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import MahiRobot from '../MahiRobot';
import useReducedMotion from '../../hooks/useReducedMotion';
import { buildTourSteps } from './tourSteps';

const ORB = 64;          // the travelling orb, smaller than the 80px seated one
const CARD_W = 300;      // caption width; fits inside a 360px screen with margin
const CARD_H = 176;      // generous estimate, only used to keep it on screen
const PAD = 10;          // breathing room between the spotlight and its target
const GAP = 14;          // between spotlight, orb and caption

/** Where the spotlight goes for a step, or null for a centred, un-anchored one. */
function measure(target) {
  if (!target) return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // An element scrolled out of view, or collapsed, is not worth pointing at.
  if (r.width < 8 || r.height < 8) return null;
  return {
    top: r.top - PAD, left: r.left - PAD,
    width: r.width + PAD * 2, height: r.height + PAD * 2,
    // Circular controls (the orb, the wardrobe button) stay circular.
    radius: Math.abs(r.width - r.height) < 6 ? 9999 : 24,
  };
}

/**
 * Stack the orb and its caption on whichever side of the spotlight has room,
 * then clamp both inside the viewport. Below is preferred for something in the
 * top half of the screen and above for the bottom half, so she never covers
 * the thing she is currently talking about.
 */
function layout(spot, vw, vh) {
  if (!spot) {
    return { orbX: vw / 2 - ORB / 2, orbY: vh * 0.34, cardX: vw / 2 - CARD_W / 2, cardY: vh * 0.34 + ORB + GAP };
  }
  const below = spot.top + spot.height / 2 < vh / 2;
  const blockH = ORB + GAP + CARD_H;
  let top = below ? spot.top + spot.height + GAP : spot.top - GAP - blockH;
  top = Math.max(16, Math.min(top, vh - blockH - 16));

  const centreX = spot.left + spot.width / 2;
  return {
    orbX: Math.max(16, Math.min(centreX - ORB / 2, vw - ORB - 16)),
    orbY: top,
    cardX: Math.max(12, Math.min(centreX - CARD_W / 2, vw - CARD_W - 12)),
    cardY: top + ORB + GAP,
  };
}

export default function DiscoveryTour({ user, onDone }) {
  const reduced = useReducedMotion();
  const allSteps = useMemo(() => buildTourSteps(user), [user]);
  const [index, setIndex] = useState(0);
  const [frame, setFrame] = useState(() => ({ vw: window.innerWidth, vh: window.innerHeight }));

  // Drop any step whose target isn't actually on screen right now, rather than
  // spotlighting empty space. Re-evaluated per render so a card arriving late
  // still gets its step.
  const steps = useMemo(
    () => allSteps.filter((s) => s.target === null || measure(s.target) !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSteps, frame],
  );

  const step = steps[Math.min(index, steps.length - 1)];
  const [spot, setSpot] = useState(() => measure(step?.target));

  // useLayoutEffect, not useEffect: measuring after paint would show one frame
  // of the spotlight at its previous position before snapping.
  useLayoutEffect(() => { setSpot(measure(step?.target)); }, [step]);

  useEffect(() => {
    const onResize = () => {
      setFrame({ vw: window.innerWidth, vh: window.innerHeight });
      setSpot(measure(step?.target));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [step]);

  const finish = useCallback(() => onDone?.(), [onDone]);
  // Advancing past the last step ends the tour. Deliberately not folded into
  // the setIndex updater: React can invoke an updater twice (StrictMode, and
  // when replaying a render), which would fire the finish side effect — and
  // its POST — more than once.
  const next = useCallback(() => {
    if (index >= steps.length - 1) { finish(); return; }
    setIndex((i) => i + 1);
  }, [index, steps.length, finish]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, finish]);

  if (!step) return null;

  const { orbX, orbY, cardX, cardY } = layout(spot, frame.vw, frame.vh);
  const isLast = index >= steps.length - 1;
  const move = reduced
    ? { duration: 0.2, ease: 'easeInOut' }
    : { type: 'spring', stiffness: 190, damping: 24, mass: 0.9 };

  return (
    <motion.div
      className="fixed inset-0 z-[70]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      onClick={next}
      role="dialog"
      aria-modal="true"
      aria-label="How MAHI works"
      data-testid="discovery-tour"
    >
      {/* The dim + cutout. `inset` is animated rather than width/height so the
          spotlight glides between targets instead of jumping. */}
      <motion.div
        className="absolute pointer-events-none"
        initial={false}
        animate={spot
          ? { top: spot.top, left: spot.left, width: spot.width, height: spot.height, borderRadius: spot.radius, opacity: 1 }
          // No target: collapse to the centre so the whole screen simply dims.
          : { top: frame.vh / 2, left: frame.vw / 2, width: 0, height: 0, borderRadius: 9999, opacity: 1 }}
        transition={move}
        style={{ boxShadow: '0 0 0 9999px rgba(15,23,42,0.74)' }}
      />
      {/* A soft ring on the spotlight itself — without it the cutout reads as a
          hole in the dim rather than as something being pointed at. */}
      {spot && (
        <motion.div
          className="absolute pointer-events-none ring-2 ring-mahiteal-300/70"
          initial={false}
          animate={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, borderRadius: spot.radius }}
          transition={move}
        />
      )}

      <motion.div
        className="absolute top-0 left-0 pointer-events-none"
        initial={false}
        animate={{ x: orbX, y: orbY }}
        transition={move}
        style={{ width: ORB, height: ORB }}
      >
        <motion.div
          className="absolute inset-0 rounded-full bg-mahiteal-400"
          style={{ filter: 'blur(10px)' }}
          animate={reduced ? { opacity: 0.4 } : { opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
          transition={reduced ? { duration: 0 } : { duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative w-full h-full rounded-full bg-white ring-4 ring-mahiteal-100 shadow-[0_16px_36px_-10px_rgba(29,162,132,0.6)] flex items-center justify-center">
          <MahiRobot size={38} mood={step.mood} />
        </div>
      </motion.div>

      <motion.div
        className="absolute top-0 left-0"
        initial={false}
        animate={{ x: cardX, y: cardY }}
        transition={move}
        style={{ width: CARD_W }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: reduced ? 0.15 : 0.28, ease: 'easeOut' }}
            // Fully opaque, not bg-white/97. At 97% a busy photo still tints
            // the card and takes the edge off slate-600 body copy — the one
            // surface here carrying real sentences is the last place to spend
            // legibility on translucency.
            className="rounded-3xl bg-white px-5 py-4 shadow-[0_28px_70px_-24px_rgba(15,23,42,0.6)]"
          >
            <h2 className="font-mahi-serif text-[22px] leading-tight text-slate-900">{step.title}</h2>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600 font-mahi-sans">{step.body}</p>

            <div className="mt-4 flex items-center gap-3">
              <div className="flex items-center gap-1.5" aria-hidden="true">
                {steps.map((s, i) => (
                  <span
                    key={s.id}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === index ? 'w-5 bg-mahiteal-500' : 'w-1.5 bg-slate-200'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="tour-next"
                className="ml-auto shrink-0 rounded-full bg-slate-900 text-white text-[13px] font-semibold font-mahi-sans px-4 h-9 active:scale-95 transition-transform"
              >
                {isLast ? 'Start swiping' : 'Next'}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Always reachable, on every step.

          Both of these float over the dim rather than over a surface of their
          own, and the dim is translucent — so what is actually behind them is
          a product photo, which can be anything from a black gown to a white
          one. Carrying their own near-opaque slate pill is what makes them
          legible independent of that. Measured against the worst case (a white
          dress under the dim): Skip went from 4.90:1 to 14.7:1, and the hint
          below from 2.95:1 — under the 4.5:1 WCAG AA floor for text this size
          — to 10.1:1. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); finish(); }}
        data-testid="tour-skip"
        className="absolute top-5 right-5 rounded-full bg-slate-900/72 backdrop-blur-md ring-1 ring-white/20 text-white text-xs font-semibold font-mahi-sans px-4 h-9 shadow-lg"
      >
        Skip
      </button>

      <div className="absolute inset-x-0 bottom-5 flex justify-center pointer-events-none">
        <p className="rounded-full bg-slate-900/60 backdrop-blur-sm px-3.5 py-1.5 text-[11px] text-white/85 font-mahi-sans">
          Tap anywhere to continue
        </p>
      </div>

      <div aria-live="polite" className="sr-only">{`${step.title}. ${step.body}`}</div>
    </motion.div>
  );
}
